/**
 * Party-facts lever D / D-13 — the `parties` tree's payload types and its CLIENT SORT.
 *
 * Leaf module: no React, so the sort is testable without mounting anything, and the renderer cannot
 * quietly grow a second ordering rule beside it.
 *
 * ⛔ **THE SORT IS A PURE FUNCTION OF THE GATED PAYLOAD AND THE VIEWER'S (mode, window)** — build-plan
 * invariant 10. Everything it reads (`materiality_rank`, `side_rank`, `substantive`, `activity`) was
 * computed by the server AFTER the entity gate, the D8 audience and the §9 evidence gate, so no
 * ordering here can reveal a party that is not in the payload or move one that is. It is also TOTAL:
 * every comparison ends in `node_id`, which is stable across reads and across viewers, so two renders
 * of one payload are identical and the pre-hydrate and hydrated views cannot disagree.
 *
 * Sorting is deliberately NOT "ask the server again with a sort param": the four activity windows come
 * down in one read precisely so switching window is a re-sort, not a round trip (spec lever D, sort
 * mode 2).
 */

/** One `{local_id, name}` ref as the gate rebuilds it. `name` is null only on an ungated cell. */
export interface PartyRefValue {
  local_id: number;
  name: string | null;
  open_profile?: { kind: string; id: number } | null;
}

/** The four windows plus the whole corpus, as `party_activity` counts them for THIS viewer. */
export interface PartyActivity {
  d7: number;
  d30: number;
  d90: number;
  lifetime: number;
  last_at: string | null;
}

/** One `(relationship_type, role)` chip, with every live fact behind it. */
export interface PartyRoleEntry {
  relationship_type: string;
  role: string | null;
  party?: string | null;
  implied: boolean;
  participation_key: string;
  subject_kind: string;
  materiality_rank?: number;
  substantive?: boolean;
  members: { assertion_id: number; revision?: number; relationship_type?: string;
             qualifiers?: Record<string, string> }[];
}

/**
 * A recorded side that disagrees with the counterpart's (P3, computed server-side).
 *
 * `axis` says WHICH rule produced it, and the two read differently (lead ruling 2026-09-05):
 *
 * * `'party'` — a PERSON against their firm, ACROSS all disciplines. No single claim axis is
 *   involved, so `relationship_type` and `role` are null and the sentence is about the deal side.
 * * `'discipline'` — a FIRM against one of its people at one shared claim axis, which `role` names.
 */
export interface PartyContradiction {
  axis: 'party' | 'discipline';
  relationship_type: string | null;
  role: string | null;
  own_sides: string[];
  other_sides: string[];
  counterpart: { name: string | null; relation: 'firm' | 'employee' };
}

export interface PartyPersonNode {
  node_id: string;
  person: PartyRefValue;
  roles: PartyRoleEntry[];
  acting_for?: PartyRefValue;
  title?: string;
  employer?: { local_id: number; name: string | null } | null;
  profile_hidden?: boolean;
  materiality_rank?: number;
  side_rank?: number;
  substantive?: boolean;
  activity?: PartyActivity;
  contradiction?: PartyContradiction[];
  capacity_source?: 'fact' | 'employment' | 'none';
}

export interface PartyGroupNode {
  node_id: string;
  /** `null` IS the trailing `Individuals` group — no sentinel id, the same rule the flat cells use. */
  org: PartyRefValue | null;
  firm_role_recorded: boolean;
  roles: PartyRoleEntry[];
  people: PartyPersonNode[];
  people_total: number;
  materiality_rank?: number;
  side_rank?: number;
  substantive?: boolean;
  activity?: PartyActivity;
  contradiction?: PartyContradiction[];
}

export interface PartyTreeValue {
  kind: 'party_tree';
  version: number;
  groups: PartyGroupNode[];
  /** The earliest message this viewer can see — what `lifetime` is bounded BY. */
  corpus_from?: string | null;
  /** Set by the per-viewer enrichment. Absent ⇒ this payload never went through it (a grid row). */
  viewer_enriched?: boolean;
  /**
   * Which dependent features are LIVE, read off the server's own flags by the same enrichment pass.
   *
   * ⛔ `party_filter` is what "show emails" is drawn from — never provider presence. The record panel
   * mounts a `CrmPartyFilterProvider` unconditionally, so provider presence was true in exactly the
   * window where lever F is dark, and the button then sent a `party` the server ignores. Absent ⇒
   * treat every flag as OFF: a payload that predates this key cannot promise a filter exists.
   */
  flags?: { party_filter?: boolean };
}

export const PARTIES_FIELD_KEY = 'parties';
export const PARTY_TREE_FIELD_TYPE = 'party_tree';
export const INDIVIDUALS_NODE_ID = 'individuals';

/** How many people show before `+N more`. Spec lever D, "Rendering": three. */
export const PEOPLE_SHOWN = 3;

export type SortMode = 'role' | 'activity';
export type SortWindow = 'd7' | 'd30' | 'd90' | 'lifetime';

export interface PartiesSort {
  mode: SortMode;
  window: SortWindow;
}

export const DEFAULT_SORT: PartiesSort = { mode: 'role', window: 'd30' };

export const SORT_WINDOWS: { key: SortWindow; label: string }[] = [
  { key: 'd7', label: '7d' },
  { key: 'd30', label: '30d' },
  { key: 'd90', label: '90d' },
  { key: 'lifetime', label: 'Lifetime' },
];

/** Sorts last — the same sentinel the server uses, so an unranked node lands in one place, not two. */
const UNRANKED = 1_000_000;

export function isPartyTree(value: unknown): value is PartyTreeValue {
  return (!!value && typeof value === 'object'
    && (value as PartyTreeValue).kind === PARTY_TREE_FIELD_TYPE
    && Array.isArray((value as PartyTreeValue).groups));
}

function nodeName(node: PartyGroupNode | PartyPersonNode): string {
  const ref = 'person' in node ? node.person : node.org;
  return (ref?.name ?? '').toLocaleLowerCase();
}

function activityIn(node: { activity?: PartyActivity }, window: SortWindow): number {
  return node.activity ? node.activity[window] ?? 0 : 0;
}

function rank(node: { materiality_rank?: number }): number {
  return node.materiality_rank ?? UNRANKED;
}

function side(node: { side_rank?: number }): number {
  return node.side_rank ?? UNRANKED;
}

/**
 * The comparator, for both levels of the tree. LOWER sorts first.
 *
 * * **role mode** — materiality, then side, then activity in the chosen window, then name. Spec §5
 *   rule 4 exactly, and the same order the server emits, so role mode re-states the server's answer
 *   rather than replacing it — the two agreeing is the point.
 *
 *   ⚠ **"A no-op re-sort" is what this used to claim, and it is not exactly true** (adversarial
 *   review 2026-09-05, B-13). The two NAME comparisons differ: the server sorts on `casefold()` plus
 *   Python's codepoint order ([`party_tree_view`](../../../../backend/app/crm/party_tree_view.py)),
 *   this sorts on `toLocaleLowerCase()` plus `localeCompare()` with no locale argument. For ASCII
 *   names they agree; for non-ASCII they do not (`Ålborg` sorts after `Zurich` on the server and near
 *   `Acme` in the browser) and the browser's answer varies with the runtime's locale. Harmless —
 *   BOTH renders are client-sorted by this function, so invariant 11 ("the client never re-orders
 *   into a different answer than the one it was served") holds and the pre- and post-hydrate payloads
 *   cannot disagree. It is recorded rather than fixed because the client's order is the one the
 *   operator reads, and `localeCompare` is the right answer for a human reading a list of names; the
 *   claim was the wrong half.
 * * **activity mode** — D-6: `substantive` parties are PINNED above the rest whatever their traffic,
 *   because "the busiest correspondent on the deal" is not the same claim as "the deal's banker".
 *   Within each half, activity descends; ties fall back to materiality, then name.
 *
 * Every branch ends in `node_id`, so the order is TOTAL and two renders of one payload are identical
 * (invariant 10). A comparator that could return 0 for two distinct nodes would leave their order to
 * the engine's sort stability and, through it, to the order the server happened to serialise.
 */
function compareNodes(
  a: PartyGroupNode | PartyPersonNode, b: PartyGroupNode | PartyPersonNode, sort: PartiesSort,
): number {
  if (sort.mode === 'activity') {
    // LOWER sorts first, so a SUBSTANTIVE node contributes 0 and a non-substantive one 1.
    const pin = Number(!(a.substantive ?? false)) - Number(!(b.substantive ?? false));
    if (pin !== 0) return pin;
    const traffic = activityIn(b, sort.window) - activityIn(a, sort.window);
    if (traffic !== 0) return traffic;
    return rank(a) - rank(b)
      || nodeName(a).localeCompare(nodeName(b))
      || a.node_id.localeCompare(b.node_id);
  }
  return rank(a) - rank(b)
    || side(a) - side(b)
    || activityIn(b, sort.window) - activityIn(a, sort.window)
    || nodeName(a).localeCompare(nodeName(b))
    || a.node_id.localeCompare(b.node_id);
}

/**
 * The tree, re-ordered for one viewer's choice. Returns a NEW value; the input is not mutated.
 *
 * ⚠ **`Individuals` stays LAST whatever the mode** (D-7, owner 2026-09-05: trailing, never
 * interleaved). It is not a party — it is the group for people whose firm is unknown or was withheld
 * — so ranking it against firms would be comparing a bucket with a counterparty, and in activity mode
 * a busy bucket would head the panel.
 */
export function sortPartyTree(tree: PartyTreeValue, sort: PartiesSort): PartyTreeValue {
  const groups = [...tree.groups]
    .map((group) => ({ ...group, people: [...group.people].sort((a, b) => compareNodes(a, b, sort)) }))
    .sort((a, b) => {
      const trailing = Number(a.org === null) - Number(b.org === null);
      return trailing !== 0 ? trailing : compareNodes(a, b, sort);
    });
  return { ...tree, groups };
}

/**
 * The roles a PERSON renders as chips: only those whose `(type, role)` differs from the firm's.
 *
 * Spec lever D, "Rendering": *"a role chip only where the person's set differs from the org's"*. A
 * banker at the sell-side bank repeating "Investment Banking" under the firm's own "Investment
 * Banking" heading is the noise the whole field exists to remove — but a lawyer at that bank is
 * information, and dropping it would hide a real claim.
 *
 * Compared on the PAIR, never on the role slug: `transaction_advisor · legal` and
 * `deal_participation · legal` are different claims that happen to share a word.
 *
 * ⚠ **And on the SIDE, because the label carries it** (adversarial review 2026-09-05, B-5). The chip
 * renders `groupHeading(entry.role, entry.party)` — `Legal — Sell Side` — so a person recorded
 * `transaction_advisor · legal · sell` under a firm recorded `transaction_advisor · legal · buy` had a
 * chip that would have READ DIFFERENTLY suppressed as "the same as the firm's". That is precisely the
 * case the owner's 2026-09-05 ruling routes to the ORG contradiction rule, which "reports that one and
 * names the discipline, so it is moved, not lost" — and the person's own chip is the EVIDENCE for the
 * badge that fires on the firm. Suppressing it deleted the evidence and kept the accusation.
 *
 * The comparison key is therefore the same triple the label is built from. The rule the whole helper
 * exists for is unchanged: identical claim, identical side ⇒ noise ⇒ gone.
 */
function chipKey(r: PartyRoleEntry): string {
  return `${r.relationship_type}|${r.role ?? ''}|${r.party ?? ''}`;
}

export function distinctPersonRoles(
  person: PartyPersonNode, group: PartyGroupNode,
): PartyRoleEntry[] {
  const firm = new Set(group.roles.map(chipKey));
  return person.roles.filter((r) => !firm.has(chipKey(r)));
}

/** Chips render in materiality order too, so the leading chip is the most material claim. */
export function sortRoleEntries(roles: PartyRoleEntry[]): PartyRoleEntry[] {
  return [...roles].sort((a, b) =>
    (a.materiality_rank ?? UNRANKED) - (b.materiality_rank ?? UNRANKED)
    || (a.relationship_type).localeCompare(b.relationship_type)
    || (a.role ?? '').localeCompare(b.role ?? ''));
}

// ── the per-viewer sort preference ───────────────────────────────────────────────────────────────

export const SORT_STORAGE_KEY = 'crm.parties.sort';

/**
 * The viewer's remembered (mode, window), or the default.
 *
 * ⚠ **Every read and write is wrapped**, and not defensively-for-the-sake-of-it: `localStorage` THROWS
 * on access in a private window, in a browser set to block site data, and inside a thumbnail/preview
 * context — not just returning null. An unguarded read there takes the whole panel down to show a
 * remembered chip state, which is the least important thing on the screen. Default `role` (D-13)
 * whenever anything at all goes wrong, including a stored value from a future version.
 */
export function loadPartiesSort(storage?: Storage): PartiesSort {
  try {
    const store = storage ?? window.localStorage;
    const raw = store.getItem(SORT_STORAGE_KEY);
    if (!raw) return DEFAULT_SORT;
    const parsed = JSON.parse(raw) as Partial<PartiesSort>;
    const mode: SortMode = parsed.mode === 'activity' ? 'activity' : 'role';
    const window_ = SORT_WINDOWS.some((w) => w.key === parsed.window)
      ? (parsed.window as SortWindow) : DEFAULT_SORT.window;
    return { mode, window: window_ };
  } catch {
    return DEFAULT_SORT;
  }
}

export function savePartiesSort(sort: PartiesSort, storage?: Storage): void {
  try {
    (storage ?? window.localStorage).setItem(SORT_STORAGE_KEY, JSON.stringify(sort));
  } catch {
    /* A preference that cannot be remembered is not an error worth surfacing. */
  }
}

/**
 * "Lifetime" labelled honestly. The corpus is 2026-only until the mail backload's first full walk, and
 * a chip that says "Lifetime" without saying since when invites reading absence as evidence.
 */
export function lifetimeLabel(corpusFrom: string | null | undefined): string {
  if (!corpusFrom) return 'Lifetime';
  const at = new Date(corpusFrom);
  if (Number.isNaN(at.getTime())) return 'Lifetime';
  return `Lifetime (since ${at.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })})`;
}
